/**
 * Donations & Grants — two-column workhorse view.
 *
 * Left: donation log (recent gifts to MRA, filterable by date).
 * Right: grants queue — campaigns whose funds are ready to disburse directly
 *   to the verified student. MRA is the grantor; the student is the grantee.
 *   School is context only, never a payee.
 *
 * TODO(backend): both lists come from GET /api/donations and GET /api/grants.
 *   "Send grant" fires a Stripe transfer directly to the student's connected
 *   account (or manual ACH / check kickoff).
 */

import { useState } from "react";
import { toast } from "sonner";
import { Wallet, Send, Download } from "lucide-react";
import { SectionHeader, StatusBadge, AdminButton, StatCard } from "./_ui";
import { RECENT_DONATIONS, PAYOUTS, ADMIN_METRICS } from "../../data/adminMock";
import { SustainingRollPanel } from "./SustainingRollPanel";

const fmt$ = (n) => `$${Number(n).toLocaleString()}`;

export const AdminDonations = () => {
  const [payouts, setPayouts] = useState(PAYOUTS);

  const sendPayout = (id, name) => {
    // TODO(backend): POST /api/grants/:id/send  -> Stripe transfer to the
    // student's connected account, then email the student their grant notice.
    setPayouts((prev) => prev.map((p) => p.id === id ? { ...p, status: "processing" } : p));
    toast.success(`Grant to ${name} started`, { description: "Student will be notified once the transfer clears." });
  };

  const readyTotal = payouts.filter((p) => p.status === "ready").reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Donations & grants"
        title="Money in, grants out."
        action={
          <AdminButton variant="secondary" size="sm" testid="export-donations" onClick={() => toast.success("CSV export queued")}>
            <Download className="h-3.5 w-3.5" strokeWidth={1.75} /> Export CSV
          </AdminButton>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Ready to grant" value={fmt$(readyTotal)} accent="tangerine" helper={`${payouts.filter((p) => p.status === "ready").length} students awaiting`} testid="ready-payout" />
        <StatCard label="Granted this month" value={fmt$(ADMIN_METRICS.disbursedThisMonth)} accent="grass" helper="Weekly disbursements to verified students." />
        <StatCard label="Lifetime granted" value={fmt$(ADMIN_METRICS.totalDisbursed)} accent="ink" helper={`${ADMIN_METRICS.studentsHelped} students helped`} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Donations feed */}
        <div className="rounded-lg border border-ink/10 bg-sand">
          <div className="border-b border-ink/10 px-5 py-4">
            <p className="font-serif text-lg text-ink">Recent donations</p>
            <p className="mt-0.5 text-xs text-ink/60">Live gifts across the platform.</p>
          </div>
          <ul className="divide-y divide-ink/10">
            {RECENT_DONATIONS.length === 0 ? (
              <li className="px-6 py-8 text-center">
                <p className="font-serif text-lg text-ink">No donations yet.</p>
                <p className="mt-1 text-xs text-ink/60">Gifts across the platform will appear here.</p>
              </li>
            ) : (
              RECENT_DONATIONS.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">{d.donor}</p>
                    <p className="mt-0.5 truncate text-xs text-ink/60">to {d.campaign} · {d.school}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-lg text-ink">{fmt$(d.amount)}</p>
                    <p className="text-[11px] text-ink/50">{d.at}</p>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* Grants queue */}
        <div className="rounded-lg border border-ink/10 bg-sand">
          <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4">
            <div>
              <p className="font-serif text-lg text-ink">Grants queue</p>
              <p className="mt-0.5 text-xs text-ink/60">Closed campaigns ready to disburse to students.</p>
            </div>
            <Wallet className="h-4 w-4 text-ink/40" strokeWidth={1.75} />
          </div>
          <ul className="divide-y divide-ink/10">
            {payouts.length === 0 ? (
              <li className="px-6 py-8 text-center">
                <p className="font-serif text-lg text-ink">Queue empty.</p>
                <p className="mt-1 text-xs text-ink/60">Closed campaigns ready to disburse to students will appear here.</p>
              </li>
            ) : (
              payouts.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">{p.student}</p>
                    <p className="mt-0.5 truncate text-xs text-ink/60">Enrolled at {p.school} · Closed {p.closedAt}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-serif text-lg text-ink">{fmt$(p.amount)}</p>
                    <StatusBadge status={p.status} />
                    {p.status === "ready" && (
                      <AdminButton size="sm" onClick={() => sendPayout(p.id, p.student)} testid={`send-payout-${p.id}`}>
                        <Send className="h-3 w-3" strokeWidth={1.75} /> Send
                      </AdminButton>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      <SustainingRollPanel />
    </div>
  );
};
